/*
 * Copyright 2026 OpenTrace Contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRef, useState } from 'react';
import {
  loadSummarizerStrategy,
  saveSummarizerStrategy,
  loadSummarizerLlmConfig,
  saveSummarizerLlmConfig,
} from '../config/summarization';
import {
  loadAnimationSettings,
  saveAnimationSetting,
  type AnimationSettings,
} from '../config/animation';
import type { SummarizationStrategyType } from '@opentrace/components/pipeline';
import { PanelResizeHandle } from '@opentrace/components';
import { useStore } from '../store';
import { useResizablePanel } from '../hooks/useResizablePanel';
import './SettingsDrawer.css';

// Visualization load caps. Kept well above typical indexed graph sizes so a
// graph isn't silently truncated (which orphans the nodes whose edges get cut
// and makes "indexed > shown"). The renderer is built for 100k+; users with
// pathologically large graphs can lower these here.
const DEFAULT_MAX_NODES = 50000;
const DEFAULT_MAX_EDGES = 50000;
const LS_KEY_NODES = 'ot:maxVisNodes';
const LS_KEY_EDGES = 'ot:maxVisEdges';

function loadLimit(key: string, fallback: number): number {
  const v = localStorage.getItem(key);
  if (v == null) return fallback;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/**
 * Parse a user-typed limit value, falling back to `fallback` for anything
 * unparseable or non-positive. Used by the limits inputs, which hold their
 * value as a string so intermediate keystrokes ("4" before typing "000") are
 * not clamped against `min`. Parsing happens only when the user clicks
 * "Update".
 */
function parseLimit(text: string, fallback: number): number {
  // Reject partial parses ("100abc" → 100) — require all-digits so a
  // malformed entry falls back instead of silently applying a bad limit.
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return fallback;
  const n = Number(trimmed);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

interface SettingsDrawerProps {
  onClose: () => void;
  onGraphCleared: () => void;
  onLimitsChanged?: () => void;
  onAnimationSettingsChanged?: (settings: AnimationSettings) => void;
}

export default function SettingsDrawer({
  onClose,
  onGraphCleared,
  onLimitsChanged,
  onAnimationSettingsChanged,
}: SettingsDrawerProps) {
  const { store } = useStore();
  const panelRef = useRef<HTMLDivElement>(null);
  const { width: panelWidth, handleMouseDown } = useResizablePanel({
    storageKey: 'ot_settings_drawer_width',
    defaultWidth: 420,
    minWidth: 360,
    maxWidth: 700,
    side: 'left',
    panelRef,
  });
  const [clearing, setClearing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryStrategy, setSummaryStrategy] =
    useState<SummarizationStrategyType>(loadSummarizerStrategy);
  const [llmConfig, setLlmConfig] = useState(loadSummarizerLlmConfig);
  const [llmModels, setLlmModels] = useState<string[] | null>(null);
  const [llmModelsFetching, setLlmModelsFetching] = useState(false);
  // String state so intermediate keystrokes (typing "4" before "000") are
  // not clamped to `min` while still in flight. Parsed/clamped only when
  // the user commits by clicking "Update".
  const [maxNodesText, setMaxNodesText] = useState(() =>
    String(loadLimit(LS_KEY_NODES, DEFAULT_MAX_NODES)),
  );
  const [maxEdgesText, setMaxEdgesText] = useState(() =>
    String(loadLimit(LS_KEY_EDGES, DEFAULT_MAX_EDGES)),
  );
  const [animSettings, setAnimSettings] = useState(loadAnimationSettings);

  const handleAnimToggle = (key: keyof AnimationSettings) => {
    const next = { ...animSettings, [key]: !animSettings[key] };
    setAnimSettings(next);
    saveAnimationSetting(key, next[key]);
    onAnimationSettingsChanged?.(next);
  };

  const handleClear = async () => {
    setClearing(true);
    setError(null);
    try {
      await store.clearGraph();
      setConfirmOpen(false);
      onGraphCleared();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear graph');
    } finally {
      setClearing(false);
    }
  };

  const handleStrategyChange = (strategy: SummarizationStrategyType) => {
    setSummaryStrategy(strategy);
    saveSummarizerStrategy(strategy);
  };

  const handleLlmConfigChange = (url: string, model: string) => {
    setLlmConfig({ url, model });
    saveSummarizerLlmConfig(url, model);
  };

  const fetchLlmModels = async () => {
    setLlmModelsFetching(true);
    setLlmModels(null);
    try {
      const res = await fetch(`${llmConfig.url}/v1/models`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const ids: string[] = (json.data ?? []).map((m: { id: string }) => m.id);
      setLlmModels(ids);
      if (ids.length > 0 && !ids.includes(llmConfig.model)) {
        handleLlmConfigChange(llmConfig.url, ids[0]);
      }
    } catch {
      setLlmModels([]);
    } finally {
      setLlmModelsFetching(false);
    }
  };

  // Track whether limits have been edited but not yet applied. Compare
  // parsed values rather than raw text — so leading zeros or trailing
  // spaces don't register as dirty, and unparseable input (which would
  // fall back to the saved value on apply) doesn't either.
  const savedNodes = loadLimit(LS_KEY_NODES, DEFAULT_MAX_NODES);
  const savedEdges = loadLimit(LS_KEY_EDGES, DEFAULT_MAX_EDGES);
  const parsedNodes = parseLimit(maxNodesText, savedNodes);
  const parsedEdges = parseLimit(maxEdgesText, savedEdges);
  const limitsChanged =
    parsedNodes !== savedNodes || parsedEdges !== savedEdges;

  const applyLimits = async () => {
    const nextNodes = parsedNodes;
    const nextEdges = parsedEdges;
    localStorage.setItem(LS_KEY_NODES, String(nextNodes));
    localStorage.setItem(LS_KEY_EDGES, String(nextEdges));
    // Normalize the text inputs so any unparseable input snaps back to
    // the accepted value and the dirty indicator clears.
    setMaxNodesText(String(nextNodes));
    setMaxEdgesText(String(nextEdges));
    await store.setLimits?.(nextNodes, nextEdges);
    onLimitsChanged?.();
  };

  return (
    <div
      ref={panelRef}
      className="settings-drawer"
      style={
        { '--settings-drawer-width': `${panelWidth}px` } as React.CSSProperties
      }
    >
      <PanelResizeHandle side="left" onMouseDown={handleMouseDown} />
      <div className="panel-header">
        <h3>Settings</h3>
        <button className="close-btn" onClick={onClose}>
          &times;
        </button>
      </div>

      <div className="settings-content">
        <section className="settings-section">
          <h4>Indexing</h4>
          <div className="setting-card">
            <div className="setting-info">
              <strong>Summarization</strong>
              <p>How node summaries are generated during indexing.</p>
            </div>
            <div className="toggle-group">
              <button
                type="button"
                className={`toggle-btn${summaryStrategy === 'template' ? ' active' : ''}`}
                onClick={() => handleStrategyChange('template')}
              >
                Template (Fast)
              </button>
              <button
                type="button"
                className={`toggle-btn${summaryStrategy === 'ml' ? ' active' : ''}`}
                onClick={() => handleStrategyChange('ml')}
              >
                ML Model
              </button>
              <button
                type="button"
                className={`toggle-btn${summaryStrategy === 'llm' ? ' active' : ''}`}
                onClick={() => handleStrategyChange('llm')}
              >
                Local LLM
              </button>
              <button
                type="button"
                className={`toggle-btn${summaryStrategy === 'none' ? ' active' : ''}`}
                onClick={() => handleStrategyChange('none')}
              >
                Disabled
              </button>
            </div>
            {summaryStrategy === 'llm' && (
              <div
                className="llm-summarizer-config"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                <div className="limit-row">
                  <label className="limit-label" htmlFor="sum-llm-url">
                    URL
                  </label>
                  <input
                    id="sum-llm-url"
                    type="text"
                    className="settings-select"
                    value={llmConfig.url}
                    onChange={(e) =>
                      handleLlmConfigChange(e.target.value, llmConfig.model)
                    }
                  />
                </div>
                <div className="limit-row">
                  <label className="limit-label" htmlFor="sum-llm-model">
                    Model
                  </label>
                  <div
                    style={{
                      display: 'flex',
                      gap: '6px',
                      alignItems: 'stretch',
                      flex: 1,
                    }}
                  >
                    {llmModels && llmModels.length > 0 ? (
                      <select
                        id="sum-llm-model"
                        className="settings-select"
                        value={llmConfig.model}
                        onChange={(e) =>
                          handleLlmConfigChange(llmConfig.url, e.target.value)
                        }
                      >
                        {llmModels.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id="sum-llm-model"
                        type="text"
                        className="settings-select"
                        value={llmConfig.model}
                        onChange={(e) =>
                          handleLlmConfigChange(llmConfig.url, e.target.value)
                        }
                      />
                    )}
                    <button
                      className="settings-back-btn"
                      onClick={fetchLlmModels}
                      disabled={llmModelsFetching}
                      title="Fetch available models from the server"
                      style={{ alignSelf: 'stretch' }}
                    >
                      {llmModelsFetching ? '…' : 'Fetch'}
                    </button>
                  </div>
                  {llmModels !== null && llmModels.length === 0 && (
                    <p
                      className="setting-hint"
                      style={{
                        color: 'var(--color-error, #f87171)',
                        margin: 0,
                      }}
                    >
                      Could not reach server or no models found.
                    </p>
                  )}
                </div>
              </div>
            )}
            <p className="setting-hint">
              {summaryStrategy === 'template'
                ? 'Instant summaries from code naming conventions. No model download.'
                : summaryStrategy === 'ml'
                  ? 'Higher quality for complex code. Downloads ~77MB model on first run.'
                  : summaryStrategy === 'llm'
                    ? 'Uses a local LLM (e.g. Ollama) via its OpenAI-compatible API.'
                    : 'Nodes indexed without summaries.'}
            </p>
          </div>
        </section>

        <section className="settings-section">
          <h4>Graph Limits</h4>
          <div className="setting-card">
            <div className="setting-info">
              <strong>Visualization limits</strong>
              <p>
                Maximum nodes and edges rendered in the graph view. Higher
                values let you visualize larger graphs but may slow down the
                force layout.
              </p>
            </div>
            <div className="limit-row">
              <label className="limit-label" htmlFor="max-nodes">
                Max nodes
              </label>
              <input
                id="max-nodes"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="limit-input"
                value={maxNodesText}
                onChange={(e) => setMaxNodesText(e.target.value)}
              />
            </div>
            <div className="limit-row">
              <label className="limit-label" htmlFor="max-edges">
                Max edges
              </label>
              <input
                id="max-edges"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="limit-input"
                value={maxEdgesText}
                onChange={(e) => setMaxEdgesText(e.target.value)}
              />
            </div>
            <button
              className="settings-back-btn"
              onClick={applyLimits}
              data-changed={limitsChanged || undefined}
              style={{ marginTop: 8, alignSelf: 'flex-start' }}
            >
              Update
            </button>
            <p className="setting-hint">
              Defaults: {DEFAULT_MAX_NODES.toLocaleString()} nodes /{' '}
              {DEFAULT_MAX_EDGES.toLocaleString()} edges.
            </p>
          </div>
        </section>

        <section className="settings-section">
          <h4>Graph Animation</h4>
          <div className="setting-card">
            {(
              [
                [
                  'selectionPulse',
                  'Selection Pulse',
                  'Pulsing ring on selected node',
                ] as const,
              ] as const
            ).map(([key, label, hint]) => (
              <div key={key} className="limit-row" style={{ flexWrap: 'wrap' }}>
                <label className="limit-label" style={{ flex: 1 }}>
                  {label}
                </label>
                <button
                  type="button"
                  className={`toggle-btn${animSettings[key] ? ' active' : ''}`}
                  style={{
                    width: 48,
                    flex: 'none',
                    borderRadius: 'var(--radius)',
                    border:
                      '1px solid color-mix(in oklch, var(--border) 50%, transparent)',
                  }}
                  onClick={() => handleAnimToggle(key)}
                >
                  {animSettings[key] ? 'On' : 'Off'}
                </button>
                <p
                  className="setting-hint"
                  style={{ width: '100%', marginTop: 2 }}
                >
                  {hint}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="settings-section danger-zone">
          <h4>Danger Zone</h4>
          <div className="danger-card">
            <div className="danger-info">
              <strong>Clear graph database</strong>
              <p>
                Remove all nodes and edges from LadybugDB. This action cannot be
                undone.
              </p>
            </div>
            {!confirmOpen ? (
              <button
                className="danger-btn"
                onClick={() => setConfirmOpen(true)}
              >
                Clear Database
              </button>
            ) : (
              <div className="confirm-actions">
                <span className="confirm-label">Are you sure?</span>
                <button
                  className="danger-btn confirm"
                  onClick={handleClear}
                  disabled={clearing}
                >
                  {clearing ? 'Clearing...' : 'Yes, clear everything'}
                </button>
                <button
                  className="cancel-btn"
                  onClick={() => setConfirmOpen(false)}
                  disabled={clearing}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          {error && <p className="danger-error">{error}</p>}
        </section>
      </div>
    </div>
  );
}
